<template>
  <div class="flex flex-col gap-5 px-4 w-screen lg:w-176">
    <div class="reltive flex flex-col gap-2 rounded bg-pure-white p-4 lg:p-5">
      <div><BlTextTitle4 text="Comments" /></div>
      <div class="flex items-center gap-2 flex-wrap">
        <div
          v-for="opt in filterOptions"
          :key="opt.label"
          class="h-7.5 px-2 flex items-center rounded-full cursor-pointer flex-shrink-0"
          :class="[
            opt.value === filterInput
              ? 'bg-light-purple'
              : 'bg-slate-200 hover:bg-hover-state'
          ]"
          @click="onFilter(opt.value)"
        >
          <BlTextBody2
            :text="opt.label"
            :class="[
              opt.value === filterInput ? 'text-pure-white' : 'text-slate-800'
            ]"
          />
        </div>
      </div>
    </div>
    <div v-if="fetchLoading" class="h-100 grid place-items-center">
      <div class="blum-loader"></div>
    </div>
    <div
      v-else-if="!(sessionComments || []).length"
      class="flex flex-col gap-4 rounded bg-pure-white p-4 lg:p-5 items-center"
    >
      <BlTextBody2 text="Comment(s) not found." />
    </div>
    <SessionComment
      v-else
      v-for="(comment, idx) in sessionComments"
      :key="idx"
      :comment="comment"
      :disabledAction="disabledAction"
      @on-filter="onFilter"
    />
    <div v-if="!fetchLoading && filterInput !== 'target' && !disabledAction">
      <div
        v-if="!newOpened"
        class="flex items-center justify-center rounded bg-pure-white p-2 hover:bg-hover-state cursor-pointer"
        @click="openNew"
      >
        <BlTextSubtitle3
          text="Add new comment"
          class="text-light-purple hover:text-dark-purple-200"
        />
      </div>
      <div v-else class="flex flex-col gap-4 rounded bg-pure-white p-4 lg:p-5">
        <div class="flex items-center gap-2">
          <div
            v-for="opt in typeOptions"
            :key="opt.label"
            class="h-7.5 px-2 flex items-center rounded-full cursor-pointer"
            :class="[
              opt.value === typeInput
                ? 'bg-light-purple'
                : 'bg-slate-200 hover:bg-hover-state'
            ]"
            @click="onChangeType(opt.value)"
          >
            <BlTextBody2
              :text="opt.label"
              :class="[
                opt.value === typeInput ? 'text-pure-white' : 'text-slate-800'
              ]"
            />
          </div>
        </div>
        <BlInputText
          v-if="typeInput === 'general'"
          :input="bodyInput"
          @changed="inputHandler"
        />
        <div v-if="typeInput === 'assessment'" class="flex flex-col gap-4">
          <BlInputText :input="antecedentInput" @changed="inputHandler" />
          <BlInputText :input="behaviorInput" @changed="inputHandler" />
          <BlInputText :input="consequenceInput" @changed="inputHandler" />
        </div>
        <div
          class="pt-2 flex items-center justify-end gap-2 border-t border-slate-400"
        >
          <BlInputButton
            type="outlined"
            dense
            label="Cancel"
            @clicked="newOpened = false"
          />
          <BlInputButton
            v-if="typeInput === 'general'"
            label="Add comment"
            icon="plus"
            dense
            :disabled="isDisabledSubmit"
            :loading="newLoading"
            @clicked="onCreate"
          />
          <BlInputButton
            v-if="typeInput === 'assessment'"
            label="Add ABC data"
            icon="plus"
            dense
            :disabled="isDisabledSubmit"
            :loading="newLoading"
            @clicked="onCreateABC"
          />
        </div>
      </div>
    </div>
    <div class="h-6 flex-shrink-0"></div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import SessionComment from "./Item.vue";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "SessionRecordComments",
  components: {
    SessionComment,
    BlTextTitle4,
    BlTextSubtitle3,
    BlTextBody2,
    BlInputText,
    BlInputButton
  },
  props: ["sessionId"],
  data() {
    return {
      fetchLoading: false,
      filterInput: "", // (all) | general | target | assessment | mine
      filterOptions: [
        { value: "", label: "All comments" },
        { value: "general", label: "General comments" },
        { value: "assessment", label: "ABC data" },
        { value: "target", label: "From targets" },
        { value: "mine", label: "Added by you" }
      ],
      newOpened: false,
      newLoading: false,
      typeInput: "general", // general | assessment
      bodyInput: {
        model: "",
        placeholder: "Type your comment",
        textarea: true
      },
      antecedentInput: {
        label: "Antecedent",
        model: "",
        placeholder: "Type your comment",
        textarea: true
      },
      behaviorInput: {
        label: "Behavior",
        model: "",
        placeholder: "Type your comment",
        textarea: true
      },
      consequenceInput: {
        label: "Consequence",
        model: "",
        placeholder: "Type your comment",
        textarea: true
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser", "session", "sessionComments"]),
    typeOptions() {
      const general = { value: "general", label: "General comment" };
      const assessment = { value: "assessment", label: "ABC data" };
      if (this.filterInput === "assessment") return [assessment];
      if (this.filterInput === "general") return [general];
      return [general, assessment];
    },
    isDisabledSubmit() {
      if (this.typeInput === "general") {
        if (!this.bodyInput.model) return true;
      }
      if (this.typeInput === "assessment") {
        if (
          !this.antecedentInput.model &&
          !this.behaviorInput.model &&
          !this.consequenceInput.model
        ) {
          return true;
        }
      }
      return false;
    },
    disabledAction() {
      if (!this.session) return true;
      return (
        this.session.status === "completed" ||
        this.session.status === "cancelled"
      );
    }
  },
  watch: {
    sessionId(val) {
      if (val) this.fetchSessionComments();
    }
  },
  mounted() {
    if (this.sessionId) this.fetchSessionComments();
  },
  methods: {
    ...mapActions([
      "getSessionComments",
      "createSessionComment",
      "createAssessment"
    ]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    async fetchSessionComments() {
      this.newOpened = false;
      const data = {
        id: this.session.id,
        params: this.filterInput ? `?filter_by=${this.filterInput}` : ""
      };

      this.fetchLoading = true;
      await this.getSessionComments(data);
      this.fetchLoading = false;
    },
    onFilter(val) {
      const isDiff = val !== this.filterInput;
      this.filterInput = val;
      if (isDiff) this.fetchSessionComments();
      if (val === "assessment") this.typeInput = "assessment";
      else this.typeInput = "general";
    },
    openNew() {
      this.bodyInput.model = "";
      this.antecedentInput.model = "";
      this.behaviorInput.model = "";
      this.consequenceInput.model = "";
      this.newOpened = true;
    },
    onChangeType(val) {
      const isDiff = val !== this.typeInput;
      this.typeInput = val;
      if (isDiff) {
        this.bodyInput.model = "";
        this.antecedentInput.model = "";
        this.behaviorInput.model = "";
        this.consequenceInput.model = "";
      }
    },
    onCreate() {
      this.newLoading = true;
      const data = {
        id: this.session.id,
        session_comment: {
          user_id: this.currentUser.id,
          body: this.bodyInput.model
        }
      };
      this.createSessionComment(data).then(({ success }) => {
        this.newLoading = false;
        if (!success) return;
        this.newOpened = false;
        this.bodyInput.model = "";
      });
    },
    onCreateABC() {
      this.newLoading = true;
      const data = {
        id: this.session.client_id,
        assessment: {
          session_id: this.session.id,
          antecedent: this.antecedentInput.model,
          behavior: this.behaviorInput.model,
          consequence: this.consequenceInput.model,
          type: "Assessment::InSession"
        }
      };
      this.createAssessment(data).then(({ success }) => {
        this.newLoading = false;
        if (!success) return;
        this.newOpened = false;
        this.antecedentInput.model = "";
        this.behaviorInput.model = "";
        this.consequenceInput.model = "";
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/sessions/comment/RecordComments.vue